// React imports
import React, { useEffect, useState, useMemo } from "react";

// External library imports
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Slider,
  InputAdornment,
} from "@mui/material";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "utils/paginationConfig";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StarIcon from "@mui/icons-material/Star";
import FeedbackIcon from "@mui/icons-material/Feedback";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import { RootState } from "../../../store/Store";
import {
  fetchData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { hr360FeedbackDashboardUrl, hr360FeedbackDetailUrl, hr360FeedbackExportUrl } from "../../../Service/Apis";
import PageTitle from "../../../components/Page Component/PageTitle";
import { formatDate } from "../utils";

interface AverageRatings {
  communication: number;
  support: number;
  recognition: number;
  development: number;
  leadership: number;
  overall: number;
}

interface ManagerStatistics {
  manager_id: string;
  manager_name: string;
  manager_email: string;
  manager_designation: string;
  feedback_count: number;
  average_ratings: AverageRatings;
  rating_distribution?: {
    excellent: number;
    good: number;
    average: number;
    poor: number;
  };
}

interface FeedbackItem {
  feedback_id: string;
  employee_name: string;
  employee_email: string;
  manager_name: string;
  manager_email: string;
  calendar_year: number;
  submitted_date: string;
  ratings: AverageRatings;
  comments: string | null;
  is_anonymous: boolean;
}

interface HR360DashboardData {
  summary: {
    total_feedback_count: number;
    total_managers_reviewed: number;
    average_overall_rating: number;
    calendar_year: number;
  };
  manager_statistics: ManagerStatistics[];
  all_feedback: FeedbackItem[];
  category_averages: AverageRatings;
}

// Rating category configuration
const RATING_CATEGORIES = [
  {
    key: 'communication' as const,
    title: 'Communication & Transparency',
    color: '#1976d2',
  },
  {
    key: 'support' as const,
    title: 'Support & Guidance',
    color: '#388e3c',
  },
  {
    key: 'recognition' as const,
    title: 'Recognition & Appreciation',
    color: '#f57c00',
  },
  {
    key: 'development' as const,
    title: 'Career Development',
    color: '#7b1fa2',
  },
  {
    key: 'leadership' as const,
    title: 'Leadership & Decision Making',
    color: '#c2185b',
  },
];

const HR360FeedbackDashboard: React.FC = () => {
  const navigate = useNavigate();
  const selectedRole = useSelector((state: RootState) => state.auth.selectedRole);

  // Only HR/Admin should view the organization-wide 360° feedback dashboard.
  // Any other selected role (e.g. Employee) is redirected to their own feedback form.
  useEffect(() => {
    if (selectedRole !== "HR" && selectedRole !== "Admin") {
      navigate("/pms/360-feedback", { replace: true });
    }
  }, [selectedRole, navigate]);

  // useState hooks
  const [dashboardData, setDashboardData] = useState<HR360DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailData, setDetailData] = useState<any>(null);
  
  // Filters
  const [fromYear, setFromYear] = useState<number>(new Date().getFullYear());
  const [fromMonth, setFromMonth] = useState<number>(1); // January
  const [toYear, setToYear] = useState<number>(new Date().getFullYear());
  const [toMonth, setToMonth] = useState<number>(12); // December
  const [selectedManager, setSelectedManager] = useState<string>("all");
  const [selectedDesignation, setSelectedDesignation] = useState<string>("all");
  const [minRating, setMinRating] = useState<number>(0);
  const [minRatingLocal, setMinRatingLocal] = useState<number>(0);
  const [hasCommentsOnly, setHasCommentsOnly] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  // Table pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [orderBy, setOrderBy] = useState<keyof FeedbackItem>("submitted_date");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  // Other hooks
  const dispatch: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> = useDispatch();
  const showToast = useCustomToast();

  // Utility Functions
  const getRatingColor = (rating: number): string => {
    if (rating >= 4.5) return "#2e7d32"; // Excellent - Dark Green
    if (rating >= 4.0) return "#388e3c"; // Very Good - Green
    if (rating >= 3.5) return "#689f38"; // Good - Light Green
    if (rating >= 3.0) return "#fbc02d"; // Average - Yellow
    if (rating >= 2.5) return "#f57c00"; // Below Average - Orange
    return "#d32f2f"; // Needs Improvement - Red
  };

  const getRatingLabel = (rating: number): string => {
    if (rating >= 4.5) return "Excellent";
    if (rating >= 4.0) return "Very Good";
    if (rating >= 3.5) return "Good";
    if (rating >= 3.0) return "Average";
    if (rating >= 2.5) return "Below Average";
    return "Needs Improvement";
  };

  // Functions
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Add date range parameters
      params.append('from_year', fromYear.toString());
      params.append('from_month', fromMonth.toString());
      params.append('to_year', toYear.toString());
      params.append('to_month', toMonth.toString());
      
      if (selectedManager !== "all") params.append('manager_id', selectedManager);
      if (selectedDesignation !== "all") params.append('designation', selectedDesignation);
      if (minRating > 0) params.append('min_rating', minRating.toString());
      if (hasCommentsOnly) params.append('has_comments', 'true');

      const url = `${hr360FeedbackDashboardUrl}?${params.toString()}`;
      const response = await dispatch(fetchData(url)) as any;

      if (response && typeof response === 'object') {
        setDashboardData(response as HR360DashboardData);
      } else {
        showToast("Failed to load dashboard data", "error", true);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load dashboard data";
      showToast(errorMessage, "error", true);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbackDetail = async (feedbackId: string) => {
    setDetailLoading(true);
    setDetailDialogOpen(true);
    try {
      const response = await dispatch(fetchData(hr360FeedbackDetailUrl(feedbackId)));
      if (response && typeof response === 'object') {
        setDetailData(response);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load feedback details";
      showToast(errorMessage, "error", true);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      // Add date range parameters for export
      params.append('from_year', fromYear.toString());
      params.append('from_month', fromMonth.toString());
      params.append('to_year', toYear.toString());
      params.append('to_month', toMonth.toString());
      
      if (selectedManager !== "all") params.append('manager_id', selectedManager);
      if (selectedDesignation !== "all") params.append('designation', selectedDesignation);

      const url = `${hr360FeedbackExportUrl}?${params.toString()}`;
      const token = localStorage.getItem("token");
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `360-feedback-${fromYear}-${fromMonth}-to-${toYear}-${toMonth}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        showToast("Export started successfully", "success", true);
      } else {
        showToast("Failed to export data", "error", true);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to export data";
      showToast(errorMessage, "error", true);
    }
  };

  const handleViewDetail = (feedback: FeedbackItem) => {
    setSelectedFeedback(feedback);
    fetchFeedbackDetail(feedback.feedback_id);
  };

  const handleSort = (property: keyof FeedbackItem) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Filtered and sorted feedback
  const filteredFeedback = useMemo(() => {
    if (!dashboardData) return [];

    let filtered = [...dashboardData.all_feedback];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (fb) =>
          fb.employee_name.toLowerCase().includes(query) ||
          fb.manager_name.toLowerCase().includes(query) ||
          fb.employee_email.toLowerCase().includes(query) ||
          fb.manager_email.toLowerCase().includes(query)
      );
    }

    // Comments filter
    if (hasCommentsOnly) {
      filtered = filtered.filter((fb) => fb.comments && fb.comments.trim().length > 0);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: string | number | undefined = a[orderBy as keyof FeedbackItem] as string | number | undefined;
      let bValue: string | number | undefined = b[orderBy as keyof FeedbackItem] as string | number | undefined;

      if (orderBy === "submitted_date") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      const aVal = aValue ?? 0;
      const bVal = bValue ?? 0;

      if (order === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [dashboardData, searchQuery, hasCommentsOnly, orderBy, order]);

  // Paginated feedback
  const paginatedFeedback = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredFeedback.slice(start, start + rowsPerPage);
  }, [filteredFeedback, page, rowsPerPage]);

  // Chart data
  const categoryChartData = useMemo(() => {
    if (!dashboardData) return [];
    return RATING_CATEGORIES.map((cat) => ({
      name: cat.title.split(" ")[0], // Short name for chart
      fullName: cat.title,
      rating: dashboardData.category_averages[cat.key] || 0,
    }));
  }, [dashboardData]);

  const managerChartData = useMemo(() => {
    if (!dashboardData) return [];
    
    // Group by manager_id to ensure uniqueness
    const uniqueManagersMap = new Map();
    dashboardData.manager_statistics.forEach((mgr) => {
      if (!uniqueManagersMap.has(mgr.manager_id)) {
        uniqueManagersMap.set(mgr.manager_id, mgr);
      }
    });
    
    return Array.from(uniqueManagersMap.values())
      .sort((a, b) => b.average_ratings.overall - a.average_ratings.overall)
      .slice(0, 5)
      .map((mgr) => ({
        name: mgr.manager_name, // Use full name instead of first name only
        rating: mgr.average_ratings.overall,
      }));
  }, [dashboardData]);

  const uniqueManagersForFilter = useMemo(() => {
    if (!dashboardData) return [];
    
    // Group by manager_id to ensure uniqueness
    const uniqueManagersMap = new Map();
    dashboardData.manager_statistics.forEach((mgr) => {
      if (!uniqueManagersMap.has(mgr.manager_id)) {
        uniqueManagersMap.set(mgr.manager_id, mgr);
      }
    });
    
    return Array.from(uniqueManagersMap.values());
  }, [dashboardData]);

  // Helper function to generate year options
  const getYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i);
    }
    return years;
  };

  // Helper function to get month options
  const getMonthOptions = (): { value: number; label: string }[] => {
    return [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' },
    ];
  };

  // useEffect hooks
  useEffect(() => {
    fetchDashboardData();
  }, [fromYear, fromMonth, toYear, toMonth, selectedManager, selectedDesignation, minRating, hasCommentsOnly]);

  useEffect(() => {
    setMinRatingLocal(minRating);
  }, [minRating]);

  // Return JSX
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
        <Alert severity="error">Failed to load dashboard data</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Box sx={{ mb: 3 }}>
            <PageTitle>360° Feedback Dashboard</PageTitle>
          </Box>
          <Typography variant="body1" color="black">
            Comprehensive view of all 360° manager feedback, ratings, and employee comments
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          sx={{ ml: 2 }}
        >
          Export Data
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="body2" color="black" gutterBottom>
                Total Feedback
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
                {dashboardData.summary.total_feedback_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="body2" color="black" gutterBottom>
                Managers Reviewed
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
                {dashboardData.summary.total_managers_reviewed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="body2" color="black" gutterBottom>
                Average Overall Rating
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold", color: getRatingColor(dashboardData.summary.average_overall_rating) }}>
                  {dashboardData.summary.average_overall_rating.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="body2" color="black" gutterBottom>
                Calendar Year
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
                {dashboardData.summary.calendar_year}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "600" }}>
          Filters
        </Typography>
        <Grid container spacing={2}>
          {/* Date Range Filters */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "600", color: "black" }}>
              Date Range (From - To)
            </Typography>
          </Grid>
          
          {/* From Date */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>From Year</InputLabel>
              <Select
                value={fromYear}
                label="From Year"
                onChange={(e) => setFromYear(e.target.value as number)}
              >
                {getYearOptions().map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>From Month</InputLabel>
              <Select
                value={fromMonth}
                label="From Month"
                onChange={(e) => setFromMonth(e.target.value as number)}
              >
                {getMonthOptions().map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* To Date */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>To Year</InputLabel>
              <Select
                value={toYear}
                label="To Year"
                onChange={(e) => setToYear(e.target.value as number)}
              >
                {getYearOptions().map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>To Month</InputLabel>
              <Select
                value={toMonth}
                label="To Month"
                onChange={(e) => setToMonth(e.target.value as number)}
              >
                {getMonthOptions().map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Other Filters */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Manager</InputLabel>
              <Select
                value={selectedManager}
                label="Manager"
                onChange={(e) => setSelectedManager(e.target.value)}
              >
                <MenuItem value="all">All Managers</MenuItem>
                {uniqueManagersForFilter.map((mgr) => (
                  <MenuItem key={mgr.manager_id} value={mgr.manager_id}>
                    {mgr.manager_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Designation</InputLabel>
              <Select
                value={selectedDesignation}
                label="Designation"
                onChange={(e) => setSelectedDesignation(e.target.value)}
              >
                <MenuItem value="all">All Designations</MenuItem>
                {Array.from(new Set(dashboardData.manager_statistics.map((m) => m.manager_designation))).map((desig) => (
                  <MenuItem key={desig} value={desig}>
                    {desig}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="body2" gutterBottom>
                Minimum Rating: {minRatingLocal.toFixed(1)}
              </Typography>
              <Slider
                value={minRatingLocal}
                onChange={(e, value) => setMinRatingLocal(value as number)}
                onChangeCommitted={(e, value) => setMinRating(value as number)}
                min={0} 
                max={5}
                step={0.1}
                marks={[
                  { value: 0, label: "0" },
                  { value: 2.5, label: "2.5" },
                  { value: 5, label: "5" },
                ]}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasCommentsOnly}
                  onChange={(e) => setHasCommentsOnly(e.target.checked)}
                />
              }
              label="Comments Only"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Category Averages Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "600" }}>
              Average Ratings by Category
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={categoryChartData}
                margin={{ top: 5, right: 20, left: 10, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  interval={0}
                  tick={{ fontSize: 12 }}
                  angle={-20}
                  textAnchor="end"
                  height={55}
                />
                <YAxis domain={[0, 5]} />
                <Tooltip
                  formatter={(value: number) => [value.toFixed(2), "Rating"]}
                  labelFormatter={(label) => {
                    const item = categoryChartData.find((d) => d.name === label);
                    return item?.fullName || label;
                  }}
                />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} />
                <Bar dataKey="rating" name="Average Rating" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Managers Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "600" }}>
              Top 5 Managers by Overall Rating
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={managerChartData}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 5]} />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={160}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value: string) =>
                    value.length > 18 ? `${value.slice(0, 18)}…` : value
                  }
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 9999, pointerEvents: "auto" }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div style={{ padding: '10px', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px' }}>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
                          <p style={{ margin: '5px 0 0 0' }}>Rating: {data.rating.toFixed(2)}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="rating" fill="#388e3c" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* All Feedback Table */}
      <Paper elevation={2}>
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "600" }}>
            All Feedback ({filteredFeedback.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Employee</strong>
                </TableCell>
                <TableCell>
                  <strong>Manager</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Overall Rating</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Year</strong>
                </TableCell>
                <TableCell>
                  <strong>Submitted Date</strong>
                </TableCell>
                <TableCell>
                  <strong>Comments</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFeedback.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="black" sx={{ py: 3 }}>
                      No feedback found matching your filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFeedback.map((feedback) => (
                  <TableRow key={feedback.feedback_id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                          {feedback.employee_name}
                        </Typography>
                        <Typography variant="caption" color="black">
                          {feedback.employee_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                          {feedback.manager_name}
                        </Typography>
                        <Typography variant="caption" color="black">
                          {feedback.manager_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={feedback.ratings.overall.toFixed(2)}
                        size="small"
                        sx={{
                          bgcolor: getRatingColor(feedback.ratings.overall),
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{feedback.calendar_year}</TableCell>
                    <TableCell>{formatDate(feedback.submitted_date)}</TableCell>
                    <TableCell>
                      {feedback.comments ? (
                        <Chip
                          label="Has Comments"
                          size="small"
                          color="primary"
                          icon={<FeedbackIcon />}
                        />
                      ) : (
                        <Typography variant="body2" color="black">
                          No comments
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetail(feedback)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <StandardTablePagination
          component="div"
          count={filteredFeedback.length}
          page={page}
          onPageChange={(_e: unknown, newPage: number) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setDetailData(null);
          setSelectedFeedback(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              360° Feedback Details
            </Typography>
            {selectedFeedback && (
              <Chip
                label={`Rating: ${selectedFeedback.ratings.overall.toFixed(2)}`}
                sx={{
                  bgcolor: getRatingColor(selectedFeedback.ratings.overall),
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailData ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="black">
                    Employee
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {detailData.employee_name || selectedFeedback?.employee_name}
                  </Typography>
                  <Typography variant="caption" color="black">
                    {detailData.employee_email || selectedFeedback?.employee_email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="black">
                    Manager
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {detailData.manager_name || selectedFeedback?.manager_name}
                  </Typography>
                  <Typography variant="caption" color="black">
                    {detailData.manager_email || selectedFeedback?.manager_email}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: "600" }}>
                Ratings Breakdown
              </Typography>
              <Grid container spacing={2}>
                {RATING_CATEGORIES.map((cat) => (
                  <Grid item xs={12} sm={6} key={cat.key}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="black" gutterBottom>
                          {cat.title}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: "bold",
                              color: getRatingColor(
                                detailData.ratings?.[cat.key] || selectedFeedback?.ratings[cat.key] || 0
                              ),
                            }}
                          >
                            {(
                              detailData.ratings?.[cat.key] || selectedFeedback?.ratings[cat.key] || 0
                            ).toFixed(2)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {(detailData.comments || selectedFeedback?.comments) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
                    Comments
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                    <Typography variant="body1">
                      {detailData.comments || selectedFeedback?.comments}
                    </Typography>
                  </Paper>
                </>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load feedback details</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setDetailData(null);
            setSelectedFeedback(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HR360FeedbackDashboard;

